// src/components/charts/BarChart.jsx
import React, { useMemo, useState } from "react";
import { Colors } from "../../constants/Colors";

const SERIES = [
  { key: "active",    label: "Active",    color: Colors.SECONDARY_BLUE },
  { key: "completed", label: "Completed", color: Colors.SUCCESS },
  { key: "pending",   label: "Pending",   color: Colors.WARNING },
  { key: "cancelled", label: "Cancelled", color: Colors.RED },
];

export default function BarChart({ data = [], height = 240 }) {
  const [hover, setHover] = useState(null);

  const { max, ticks } = useMemo(() => {
    const m = Math.max(1, ...data.map((d) => d.total));
    const nice = Math.ceil(m / 5) * 5 || 5;
    const step = nice / 4;
    return { max: nice, ticks: [0, step, step * 2, step * 3, nice] };
  }, [data]);

  const pad = { top: 12, right: 12, bottom: 36, left: 40 };
  const vbW = 720;
  const vbH = height;
  const plotW = vbW - pad.left - pad.right;
  const plotH = vbH - pad.top - pad.bottom;

  const bandW = plotW / Math.max(1, data.length);
  const barW = Math.min(48, bandW * 0.55);

  return (
    <div className="chart-wrap" style={{ position: "relative" }}>
      <svg
        viewBox={`0 0 ${vbW} ${vbH}`}
        width="100%"
        height={vbH}
        role="img"
        aria-label="Monthly rental activity"
        style={{ display: "block" }}
      >
        {ticks.map((t, i) => {
          const y = pad.top + plotH - (t / max) * plotH;
          return (
            <g key={i}>
              <line
                x1={pad.left}
                x2={vbW - pad.right}
                y1={y}
                y2={y}
                stroke={Colors.BORDER_GRAY}
                strokeDasharray={i === 0 ? "0" : "3 4"}
              />
              <text
                x={pad.left - 8}
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fill={Colors.GRAY}
              >
                {t}
              </text>
            </g>
          );
        })}

        {data.map((d, i) => {
          const x = pad.left + i * bandW + (bandW - barW) / 2;
          let yCursor = pad.top + plotH;
          const isHover = hover === i;
          return (
            <g
              key={d.month}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              <rect
                x={pad.left + i * bandW}
                y={pad.top}
                width={bandW}
                height={plotH}
                fill={isHover ? Colors.LIGHT_GRAY : "transparent"}
              />
              {SERIES.map((s) => {
                const v = d[s.key] || 0;
                if (!v) return null;
                const h = (v / max) * plotH;
                yCursor -= h;
                return (
                  <rect
                    key={s.key}
                    x={x}
                    y={yCursor}
                    width={barW}
                    height={h}
                    fill={s.color}
                    rx="2"
                    opacity={isHover ? 1 : 0.9}
                  >
                    <title>{`${s.label}: ${v}`}</title>
                  </rect>
                );
              })}
              <text
                x={pad.left + i * bandW + bandW / 2}
                y={vbH - 14}
                textAnchor="middle"
                fontSize="12"
                fill={Colors.TEXT_LIGHT}
              >
                {d.month}
              </text>
            </g>
          );
        })}
      </svg>

      {hover !== null && (
        <div className="chart-tooltip" role="status">
          <strong>{data[hover].month}</strong>
          <div className="chart-tooltip-rows">
            {SERIES.map((s) => (
              <div key={s.key} className="chart-tooltip-row">
                <span className="legend-dot" style={{ background: s.color }} />
                <span>{s.label}</span>
                <b>{data[hover][s.key] || 0}</b>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}